import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import MultiPose from '../../PackageBuilders/MultiPose';
import { useCustomer } from '../../../contexts/CustomerContext';
import { getAppropriateBg } from '../_ElementaryMultiPose/MultiPosePackageBuilder/steps/FavoritePoseSelection';
import PortraitsGallery from '../../PortraitsGallery';
import useBuyAllOption from '../../../hooks/useBuyAllOption';
import useLocalStorage from '../../../hooks/useLocalStorage';
import useBackgrounds from '../../../hooks/useBackgrounds';
import useImageOrientations from '../../../hooks/useImageOrientations';
import usePoses from '../../../hooks/usePoses';
import usePreorderPose from '../../../hooks/usePreorderPose';
import useQueryString from '../../../hooks/use-query-string';
import routes from '../../../routes';
import { OrderPackage } from '../../../models';
import OptionSelectionContent from '../OptionSelection/OptionSelectionContent';
import usePricelist from '../../../hooks/usePricelist';
import useRetouchingExtra from '../../../hooks/useRetouchingExtra';
import useAddons from '../../../hooks/useAddons';
import QueryKeys from '../../../utils/QueryKeys';
import SinglePoseMutator from './SinglePoseMutator';
import useImageTypes from '../../../hooks/useImageTypes';
import useMultiSeasonPoses from "../../../hooks/useMultiSeasonPoses";
import useHolidayPricelist from "../../../hooks/useHolidayPricelist";
import GalleryIntro from '../../GalleryIntro';
import introPoses from '../../../utils/introPoses';
import introHeroPoses from '../../../utils/introHeroPoses';
import gradeLabel, { portraitsHeadlineParts } from '../../../utils/gradeLabel';
import usePackagePoses from '../../../hooks/usePackagePoses';
import useOfferCoupon from '../../../hooks/useOfferCoupon';
import { freeMagnetsStatus, isAllJpgPoses } from '../../../utils/freeMagnets';
import BuyAllCard from '../../BuyAllCard';
import { afterBuildFreeMagnetsRoute } from '../_ElementaryMultiPose/FreeMagnets/freeMagnetsVisibility';
import { trackFunnel, FUNNEL_EVENTS } from '../../../utils/trackFunnel';

export default function MultiPosePackageBuilder() {
  // Routing and queries
  const { packageId } = useParams();
  const history = useHistory();
  const {
    step,
    opt: optionId,
    pastImages,
    favorites,
    preorderPoseId,
    type,
  } = useQueryString();
  const favoriteIds = [favorites].flat().filter((id) => !!id).map((id) => id.toString());

  // Data
  const { customer, setCustomer } = useCustomer();
  const [orderPackage] = useState(new OrderPackage());
  const { data: backgrounds } = useBackgrounds(packageId);
  const { data: pricelist } = usePricelist(packageId);
  const { data: poses } = pricelist?.id && (pricelist?.multipleSeasons?.length || pricelist?.displaySharedPoses) ? (
      useMultiSeasonPoses(customer.currentStudent.id, packageId)
    ) : (
    usePoses(customer.currentStudent.id, packageId, pastImages)
  )

  const { data: preorderPose } = usePreorderPose(preorderPoseId)

  useRetouchingExtra();
  const { data: addons } = useAddons(true);

  const { data: imageTypes } = useImageTypes(optionId);
  const { data: packagePoses } = usePackagePoses(packageId);
  // Measure the intro candidates here so introPoses (below) filters landscape frames out
  // of the vertical intro cards from a warm cache — nothing else measures them during the
  // intro step, so a cold cache would leak a -25.png class photo in. introPhotos recomputes
  // every render, so it heals as orientations resolve.
  useImageOrientations((poses || []).map((pose) => pose.imageUrl || pose.watermarkUrl).filter(Boolean));
  const { data: buyAllOption } = useBuyAllOption(packageId, customer?.currentSchool?.id);
  const [currentSeason] = useLocalStorage(`currentSeason-${customer?.currentStudent?.id}`);
  // The live offer for this pricelist (the same resolver OptionSelectionContent
  // uses) and the cart's free-magnets flags: the Buy All card reads its
  // price/ribbon from the offer, and the after-build detour to the magnet page
  // asks both (issue #5218).
  const { data: offer } = useOfferCoupon(customer?.currentStudent?.studentId, customer?.currentSchool?.id, packageId);
  const queryClient = useQueryClient();

  // The intro splash — the same one the senior flow plays, with the student's GRADE above
  // their name instead of "Class of 20XX". Session state, not a persisted "seen" flag.
  const [introDone, setIntroDone] = useState(false);

  const favoritePoses = favoriteIds.map((id) => (poses || []).find((pose) => pose.id === id));

  // UI
  const isFetchingPoses = useIsFetching({ queryKey: [QueryKeys.STUDENT_IMAGES] });
  const isFetchingBackgrounds = useIsFetching({ queryKey: [QueryKeys.BACKGROUNDS] });
  const isFetchingPricelist = useIsFetching({ queryKey: [QueryKeys.PRICELIST] });
  const { data: giftPricelist } = useHolidayPricelist(customer.currentSchool?.id);

  const isFetching = isFetchingPoses || isFetchingBackgrounds || isFetchingPricelist;

  // "Download All My Photos" — the same POST + confirm redirect the old favorites screen
  // did. Kept identical: it is a live revenue path, not part of the redesign.
  const handleBuyAll = async () => {
    const response = await axios.post(
      `/carts/${customer?.cart?.id}/buy_all`,
      {
        package_id: packageId,
        option_id: buyAllOption.id,
        student_id: customer.currentStudent.id,
        season: currentSeason,
      },
    );
    const orderPackageId = response.data?.order_package_id;
    // Buy All Digital qualifies for the free magnets (issue #5218): detour
    // through the magnet page on the way to confirm, deciding from the FRESH
    // flags the /buy_all response carries (never the cached cart).
    queryClient.invalidateQueries({ queryKey: [QueryKeys.CART] });
    const status = await freeMagnetsStatus({ data: response.data, cartId: customer?.cart?.id });
    history.push(afterBuildFreeMagnetsRoute({
      next: routes.confirm(orderPackageId), orderPackageId, offer, status, allJpg: isAllJpg,
    }));
  };

  const onSave = async () => {
    setCustomer({ ...customer, currentOrderPackage: orderPackage });

    // Where the built package was always headed: the add-ons chain.
    let next;
    if (giftPricelist) next = routes.holiday.builder({ optional: true, o: orderPackage.id, pid: orderPackage.packageId });
    else if (addons.length === 0) next = routes.retouching(orderPackage.id);
    else next = routes.addons(orderPackage.id);

    // The magnet-bonus page sits between the build and Announcements & Gifts
    // while the free-magnets tier is live (issue #5218 item 8). ONE gate, fed
    // the FRESH post-build flags (the Graphiti save has no custom response, so
    // freeMagnetsStatus re-reads the cart) -- the cached cart predates the
    // package and would say a stale false.
    queryClient.invalidateQueries({ queryKey: [QueryKeys.CART] });
    const status = await freeMagnetsStatus({ cartId: customer?.cart?.id });
    history.push(afterBuildFreeMagnetsRoute({
      next, orderPackageId: orderPackage.id, offer, status, allJpg: isAllJpg,
    }));
  };

  useEffect(() => {
    document.querySelectorAll('.rails-nav').forEach((node) => { node.style.display = 'none'; });

    return () => document.querySelectorAll('.rails-nav').forEach((node) => { node.style.display = 'flex'; });
  }, []);

  // Funnel log (docs/purchase_flow.md "Funnel events"). The gallery counts once
  // its poses are on screen; the option list in between ('select-package') is
  // OptionSelectionContent's to record, since every option list shares it.
  const funnelStudentId = customer?.currentStudent?.id;
  const posesShown = !isFetchingPoses && (poses?.length > 0 || !!preorderPose);
  useEffect(() => {
    if (!funnelStudentId || !packageId) return;
    const ids = {
      studentId: funnelStudentId, cartId: customer?.cart?.id, schoolId: customer?.currentSchool?.id, packageId,
    };
    if ((!step || step === 'pose-selection') && posesShown) {
      trackFunnel(FUNNEL_EVENTS.GALLERY_VIEWED, { ...ids, properties: { surface: 'multi_pose_gallery' } });
    } else if (step === 'build') {
      trackFunnel(FUNNEL_EVENTS.BUILDER_STARTED, { ...ids, optionId, properties: { surface: 'multi_pose_builder' } });
    }
  }, [step, posesShown, funnelStudentId, packageId, optionId]);

  if (!(packageId && customer.currentSchool && customer.currentStudent)) return null;
  if (isFetching) return null;
  if (backgrounds.length === 0) return null;
  if (poses?.length === 0 && !preorderPose) return null;

  const background = backgrounds[0];

  const isAllJpg = isAllJpgPoses(poses);

  // Intro cards: each pose on the backdrop the pricelist's package-poses spec gives it
  // (seniors are the only audience that overrides the backdrop with the school's own).
  const toIntroPhoto = (pose) => ({
    foregroundUrl: pose.imageUrl || pose.watermarkUrl,
    backgroundUrl: getAppropriateBg(packagePoses, pose, backgrounds, pricelist)?.imageUrl
      || backgrounds?.[0]?.imageUrl,
  });
  // The automatic pick: every vertical pose the pricelist offers; the intro spreads
  // four across it.
  const introPhotos = introPoses(poses, packagePoses).map(toIntroPhoto);
  // The CURATED pick, where this school's audience has one (JH/HS and elementary today;
  // see INTRO_HERO_POSES). Slot-ordered, and may include a landscape look — the intro
  // shapes that tile to the photo.
  const heroPoses = introHeroPoses(poses, packagePoses, customer.currentSchool);
  const introHeroPhotos = heroPoses?.length ? heroPoses.map(toIntroPhoto) : null;
  const introPreloadUrls = (introHeroPhotos || introPhotos)
    .flatMap((photo) => [photo.foregroundUrl, photo.backgroundUrl])
    .filter(Boolean);

  // Plays once, on first entry to the pose picker.
  const showIntro = (!step || step === 'pose-selection') && !introDone && introPreloadUrls.length > 0;

  return (
    <div>
      {showIntro && (
        <GalleryIntro
          studentFirstName={customer.currentStudent?.firstName}
          // Grade, not class year: "3rd Grade". gradeLabel returns null for staff and for
          // grades it can't name, and the intro omits the line entirely rather than guess.
          eyebrow={gradeLabel(customer.currentStudent?.grade)}
          heroPhotos={introHeroPhotos}
          allPhotos={introPhotos}
          preloadUrls={introPreloadUrls}
          onComplete={() => setIntroDone(true)}
        />
      )}
      {(!step || step === 'pose-selection') && (
        <PortraitsGallery
          poses={poses}
          backgrounds={backgrounds}
          packageId={packageId}
          // "Melina's Third Grade Portraits" — the grade is dropped rather than guessed
          // when we can't name it (staff, unknown), so the headline is never wrong.
          // Parts, not a flat string, so the gallery can set the child's name in a heavier
          // weight than the grade beside it.
          //
          // "Pictures", not "Portraits" — this is picture day, not a senior sitting, and
          // it's the word parents use. Only the senior flow says Portraits.
          headline={portraitsHeadlineParts(
            customer.currentStudent?.firstName,
            customer.currentStudent?.grade,
            { noun: 'Pictures' },
          )}
          // Sets the tone under the child's name without competing with it. Copy lives here,
          // not in the gallery — the gallery is shared with audiences that word this
          // differently, or not at all.
          //
          // NOT a second instruction: the gallery already says "Select your favorites."
          // just below this banner, so a directive here would only nag. This line's job is
          // warmth.
          //
          // One word off "Another year to remember", which read as a shrug: "another"
          // frames this child's year as one more in an interchangeable series, which is the
          // opposite of why a parent opened the page. "A" makes it singular — this year,
          // this child — and keeps the rest of the phrase intact.
          subhead="A year to remember"
          // No `yearbook` prop: favorites only. No cap buttons, no eligibility gate, no
          // step 1 — and backgrounds come from the pricelist's package-poses spec, since
          // seniors are the only audience that overrides the backdrop.
          favoritesKey={`favorites:${customer.currentStudent?.id}:${packageId}`}
          // Hidden for an all-JPG session, exactly as the old screen did: those poses have
          // their background baked in and aren't sold as the digital "all my photos" set.
          // The card itself (BuyAllCard) now carries the option's REAL price, the
          // discountedPrice() strike-through while a % tier is live, the offer ribbon
          // ("+2 FREE MAGNETS" / "20% OFF" / none) and the pose composited on a phone
          // + a magnet -- the banner art is the backdrop only (issue #5218 item 6).
          promo={buyAllOption && !isAllJpg ? {
            imageUrl: buyAllOption.buyAllBannerUrl,
            label: 'Download all my photos',
            onClick: handleBuyAll,
            card: (
              <BuyAllCard
                option={buyAllOption}
                offer={offer}
                onClick={handleBuyAll}
                label="Download All My Photos"
                customer={customer}
                packageId={packageId}
              />
            ),
          } : null}
          continueLabel="Continue"
          onContinue={(chosen) => history.push(
            routes.elementaryMultiPoseBuilder(packageId, { favorites: chosen, step: 'select-package' }),
          )}
        />
      )}
      {step === 'select-package' && pricelist && (
        <OptionSelectionContent
          orderPackage={orderPackage}
          foreground={favoritePoses.length > 0 ? favoritePoses[0].watermarkUrl : (preorderPose || poses[0]).watermarkUrl}
          background={background}
          multiPoseHandler={(selectedOpt) => history.push(routes.elementaryMultiPoseBuilder(packageId, { favorites: favoriteIds, preorderPoseId: preorderPoseId, opt: selectedOpt.id, step: 'build' }))}
          packageId={packageId}
          accessCodeOnly={false}
          preorderPose={preorderPose}
        />
      )}
      {step === 'build' && poses.length === 1 && isAllJpg && (
        <SinglePoseMutator
          cartId={customer.cart.id}
          onSave={onSave}
          imageTypes={imageTypes}
          orderPackage={orderPackage}
          studentId={customer.currentStudent.id}
          optionId={optionId}
          schoolId={customer.currentSchool.id}
          packageId={packageId}
          poses={poses}
        />
      )}
      {step === 'build' && (poses.length > 1 || !isAllJpg || preorderPose) && (
        <MultiPose
          cartId={customer.cart.id}
          packageId={packageId}
          schoolId={customer.currentSchool.id}
          studentId={customer.currentStudent.id}
          optionId={optionId}
          onSave={onSave}
          favoriteIds={favoriteIds}
          // This is the favorites-picker flow (URL `favorites=`), so restrict the builder
          // to the chosen poses. Other MultiPose callers (senior, edit) leave this false
          // and keep the full session -- their favoriteIds only sort/highlight.
          restrictToFavorites
          orderPackage={orderPackage}
          pastImages={pastImages}
          preorderPose={preorderPose}
        />
      )}
    </div>
  );
}
